import { useState, type FC } from 'react';
import {
  CareerExplorer,
  type CareerExplorerSelection,
  type CareerExplorerSelectorOption,
} from '@highereducation/hen-ui';

import { getCareerDataset } from '../../../composable/getCareerDataset';
import {
  mapCareerPeek,
  type CareerPeekCopy,
} from '../../../mappers/mapCareerPeek';
import { mapCareerRecommendations } from '../../../mappers/mapCareerRecommendations';
import { rerankCareerPrograms } from '../../../mappers/rerankCareerPrograms';
import type { CareerExplorerDataset } from '../../../types';
import {
  DEFAULT_CAREER_OPTIONS,
  DEFAULT_GOAL_OPTIONS,
  DEFAULT_LEVEL_OPTIONS,
} from './constants';
import { labelFor, withFallback } from './options';

type CareerLoader = (careerId: string) => CareerExplorerDataset | null;

type ReadyCareer = {
  careerId: string;
  dataset: CareerExplorerDataset;
  goalChip: string | null;
  levelChip: string | null;
};

const resolveLoader = (value: unknown): CareerLoader =>
  typeof value === 'function' ? (value as CareerLoader) : getCareerDataset;

type DslCareerExplorerProps = {
  heading?: string;
  description?: string;
  careerQuestion?: string;
  questionNote?: string;
  placeholder?: string;
  careerOptions?: CareerExplorerSelectorOption[];
  goalOptions?: CareerExplorerSelectorOption[];
  goalHeading?: string;
  levelOptions?: CareerExplorerSelectorOption[];
  levelHeading?: string;
  recommendationsTitle?: string;
  viewAllLabel?: string;
  viewAllHref?: string;
  titleTemplate?: string;
  skillsHeading?: string;
  trajectoryTitle?: string;
  trajectoryDescription?: string;
  collapsedLabel?: string;
  expandedLabel?: string;
};

const peekCopyFrom = (props: DslCareerExplorerProps): CareerPeekCopy => ({
  titleTemplate: props.titleTemplate,
  skillsHeading: props.skillsHeading,
  trajectoryTitle: props.trajectoryTitle,
  trajectoryDescription: props.trajectoryDescription,
  collapsedLabel: props.collapsedLabel,
  expandedLabel: props.expandedLabel,
});

export const DslCareerExplorer: FC<Record<string, unknown>> = (rawProps) => {
  const [ready, setReady] = useState<ReadyCareer | null>(null);

  const props = rawProps as DslCareerExplorerProps;

  const loadCareer = resolveLoader(rawProps.loadCareer);

  const careerOptions = withFallback(
    props.careerOptions,
    DEFAULT_CAREER_OPTIONS,
  );

  const goalOptions = withFallback(props.goalOptions, DEFAULT_GOAL_OPTIONS);
  const levelOptions = withFallback(props.levelOptions, DEFAULT_LEVEL_OPTIONS);

  const handleSelectionChange = (selection: CareerExplorerSelection) => {
    const { careerId, goalChip, levelChip } = selection;

    if (!careerId) {
      setReady(null);
      return;
    }

    if (ready?.careerId === careerId) {
      setReady({ ...ready, goalChip, levelChip });
      return;
    }

    let dataset: CareerExplorerDataset | null = null;

    try {
      dataset = loadCareer(careerId);
    } catch {
      return;
    }

    if (!dataset) {
      setReady(null);
      return;
    }

    setReady({
      careerId: dataset.careerId,
      dataset,
      goalChip,
      levelChip,
    });
  };

  const peek = ready
    ? mapCareerPeek(
        ready.dataset,
        labelFor(careerOptions, ready.dataset.careerId) ??
          ready.dataset.careerId,
        peekCopyFrom(props),
      )
    : undefined;

  const recommendations = ready
    ? mapCareerRecommendations({
        programs: rerankCareerPrograms(ready.dataset.programs, ready.goalChip),
        careerLabel: labelFor(careerOptions, ready.careerId) ?? ready.careerId,
        goalLabel: labelFor(goalOptions, ready.goalChip),
        levelLabel: labelFor(levelOptions, ready.levelChip),
        title: props.recommendationsTitle || undefined,
        viewAllLabel: props.viewAllLabel || undefined,
        viewAllHref: props.viewAllHref || undefined,
      })
    : undefined;

  return (
    <CareerExplorer
      heading={props.heading || undefined}
      description={props.description || undefined}
      careerQuestion={props.careerQuestion || undefined}
      questionNote={props.questionNote}
      placeholder={props.placeholder || undefined}
      careerOptions={careerOptions}
      goalOptions={goalOptions}
      goalHeading={props.goalHeading || undefined}
      levelOptions={levelOptions}
      levelHeading={props.levelHeading || undefined}
      activeCareerId={ready?.careerId ?? null}
      peek={peek}
      recommendations={recommendations}
      onSelectionChange={handleSelectionChange}
    />
  );
};
